import { FC, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import IconArrowLeft from 'src/assets/images/icons/arrow-left.svg';
import IconSearch from 'src/assets/images/icons/search-settings.svg';
import { useBilledEntities, useSelectedBilledEntities, BilledEntityItem } from 'src/use-cases/settings/useBilledEntities';
import { selectSelectedBilledEntities, setSelectedBilledEntities } from 'src/infra/stores/reducer/billedEntitiesSlice';
import { EntityList } from './EntityList';

interface BilledEntitiesMenuProps {
  onClose: () => void;
  onApply?: (selectedEntities: BilledEntityItem[]) => void;
  isVisible?: boolean;
  onToggle?: () => void; // Add toggle function prop
}

export const BilledEntitiesMenu: FC<BilledEntitiesMenuProps> = ({ onClose, onApply, onToggle, isVisible = false }): JSX.Element => {
  const dispatch = useDispatch();
  const { data: billedEntities, isLoading } = useBilledEntities();
  // Get selected entities from Redux store - these are now managed by UserDataSynchronizer

  const { entities, setEntities, toggleEntityByPath, selectAll, clearAll, getSelectedEntities } = useSelectedBilledEntities([]);

  // Check if any entity or sub-entity is selected
  const isHaveSelectedEntities = useMemo(() => {
    // Check if any main entity is selected
    const hasSelectedMainEntity = entities.some((entity) => entity.selected);
    if (hasSelectedMainEntity) return true;

    // Check if any sub-entity is selected
    const hasSelectedSubEntity = entities.some(
      (entity) => entity.subEntities?.some((subEntity) => subEntity.selected || subEntity.subEntities?.some((subSubEntity) => subSubEntity.selected))
    );

    return hasSelectedSubEntity;
  }, [entities]);

  const savedSelectedEntities = useSelector(selectSelectedBilledEntities);

  const [searchTerm, setSearchTerm] = useState('');

  // Create refs for the menu element and toggle button
  const billedMenuRef = useRef<HTMLDivElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);

  // Click outside handler to close the menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const isToggleButton = toggleButtonRef.current && toggleButtonRef.current.contains(event.target as Node);

      // Don't close if clicking the toggle button - let the button's onClick handle it
      if (!isToggleButton && billedMenuRef.current && !billedMenuRef.current.contains(event.target as Node) && isVisible) {
        onClose();
      }
    };

    // Add event listener when menu is visible
    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, onClose]);

  // Selection is now managed by UserDataSynchronizer

  // Helper function to mark entities as selected based on savedSelectedEntities
  const markEntitiesAsSelected = useCallback((entities: BilledEntityItem[], savedEntities: BilledEntityItem[]): BilledEntityItem[] => {
    if (!savedEntities || savedEntities.length === 0) return entities;

    // Create a map of selected entity IDs for quick lookup
    const selectedIdsMap = new Map<number, boolean>();

    // Recursively collect all entity IDs (including subentities) from savedEntities
    const collectAllEntityIds = (items: BilledEntityItem[]) => {
      items.forEach((entity) => {
        selectedIdsMap.set(entity.id, true);

        // Process subentities recursively
        if (entity.subEntities?.length > 0) {
          collectAllEntityIds(entity.subEntities);
        }
      });
    };

    // Collect all entity IDs from savedEntities
    collectAllEntityIds(savedEntities);

    // Recursively mark entities as selected
    const markSelected = (items: BilledEntityItem[]): BilledEntityItem[] =>
      items.map((entity) => {
        const isSelected = selectedIdsMap.has(entity.id);
        const updatedSubEntities = entity.subEntities?.length ? markSelected(entity.subEntities) : entity.subEntities;

        return {
          ...entity,
          selected: isSelected,
          subEntities: updatedSubEntities,
        };
      });

    return markSelected(entities);
  }, []);

  // Initialize entities from the data already processed by UserDataSynchronizer
  useEffect(() => {
    // Early returns to reduce nesting
    if (!billedEntities) return;

    // Handle empty entities case
    if (billedEntities.length === 0) {
      setEntities([]);
      return;
    }

    // Apply selections from savedSelectedEntities if available
    if (savedSelectedEntities.length > 0) {
      // Create a deep copy of billedEntities to avoid mutation issues
      const entitiesWithSelections = markEntitiesAsSelected(billedEntities, savedSelectedEntities);
      setEntities(entitiesWithSelections);
    } else {
      setEntities(billedEntities);
    }
  }, [billedEntities, savedSelectedEntities, setEntities, markEntitiesAsSelected]);

  // Filter entities based on search term
  const filterEntitiesRecursively = (items: BilledEntityItem[]): BilledEntityItem[] => {
    if (!searchTerm.trim()) return items; // If no search term, return all entities

    return items
      .map((entity) => {
        // Check if this entity matches the search term
        const matchesMain = entity.name.toLowerCase().includes(searchTerm.toLowerCase()) || entity.code.toLowerCase().includes(searchTerm.toLowerCase());

        // Filter sub-entities recursively
        let filteredSubEntities: BilledEntityItem[] = [];
        if (entity.subEntities && entity.subEntities.length > 0) {
          filteredSubEntities = filterEntitiesRecursively(entity.subEntities);
        }

        // If this entity matches or has matching sub-entities, include it
        if (matchesMain || filteredSubEntities.length > 0) {
          return {
            ...entity,
            subEntities: filteredSubEntities,
          };
        }

        // Otherwise, don't include this entity
        return null;
      })
      .filter(Boolean) as BilledEntityItem[];
  };

  // Apply the recursive filtering while preserving selection state
  const filteredEntities = filterEntitiesRecursively(entities);

  // Single toggle handler that works with any depth
  const handleToggle = (path: number[]) => {
    toggleEntityByPath(path);
  };

  const handleApply = () => {
    const selectedEntities = getSelectedEntities();

    // Save selected entities to Redux store
    dispatch(setSelectedBilledEntities(selectedEntities));

    if (onApply) {
      onApply(selectedEntities);
    }
    onClose();

    // Check if we should skip reload (when coming from "See Invoice" in upload status)
    const urlParams = new URLSearchParams(window.location.search);
    const skipReload = urlParams.get('skipBilledEntityReload') === 'true';

    // Reload the page to apply the new billed entity headers to all API calls
    if (selectedEntities.length > 0 && !skipReload) {
      window.location.reload();
    }
  };

  // Helper function to render content based on loading state and data availability
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className='flex items-center justify-center h-full'>
          <CircularProgress size={32} />
        </div>
      );
    }

    if (filteredEntities.length === 0) {
      return <div className='flex items-center justify-center h-full text-neutral-60'>No billed entities found</div>;
    }

    return <EntityList entities={filteredEntities} depth={0} parentPath={[]} onToggle={handleToggle} />;
  };

  return (
    <div
      ref={billedMenuRef}
      className={`absolute right-0 top-[64px] z-[999990] w-[380px] bg-white text-blue_grey-90 border border-[#DFDFDF] rounded-8 shadow-lg transform transition-all duration-300 ease-in-out ${
        isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-20 pointer-events-none'
      }`}
    >
      <form className='relative flex flex-col h-[600px] max-h-[80vh]'>
        <div className='flex flex-col'>
          <div className='flex items-center justify-between p-16'>
            <div className='flex items-center gap-8'>
              <button
                type='button'
                ref={toggleButtonRef}
                onClick={() => {
                  if (onToggle) onToggle();
                  setTimeout(() => {
                    onClose();
                  }, 50);
                }}
                aria-label='back'
                className='p-4 hover:bg-neutral-10 rounded-full transition-colors duration-200'
              >
                <img src={IconArrowLeft} alt='back' />
              </button>
              <p className='text-16 font-medium'>Billed Entities</p>
            </div>
            <div className='flex items-center gap-16'>
              <button type='button' onClick={selectAll} className='text-12 xl:text-14 font-medium text-primary'>
                Select All
              </button>
              <div className='w-1 h-12 bg-neutral-20' />
              <button type='button' onClick={clearAll} className='text-12 xl:text-14 font-medium text-neutral-60'>
                Clear
              </button>
            </div>
          </div>
          <div className='pt-4 pb-16 px-16 border-b border-neutral-20'>
            <div className='relative'>
              <img src={IconSearch} alt='search' className='absolute left-16 top-1/2 -translate-y-1/2' />
              <input
                type='text'
                placeholder='Search'
                className='w-full pl-48 pr-16 py-12 bg-neutral-10 border border-neutral-20 rounded-8'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div
          className='overflow-y-auto px-16 py-12'
          style={{ maxHeight: 'calc(80vh - 220px)', height: '380px', overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}
        >
          {renderContent()}
        </div>
        <div className='p-16 rounded-b-8 bg-neutral-10 border-t border-neutral-20'>
          <Button
            variant='contained'
            fullWidth
            onClick={handleApply}
            disabled={filteredEntities.length === 0 || !isHaveSelectedEntities}
            sx={{
              '&.Mui-disabled': {
                color: '#FFFFFF',
                cursor: 'not-allowed',
                opacity: 0.9,
              },
            }}
          >
            Apply
          </Button>
        </div>
      </form>
    </div>
  );
};
